import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import QRCode from "qrcode";
import { Printer, Calendar as CalIcon, User, ArrowLeft } from "lucide-react";
import { Card, Btn, Input } from "@/components/admin/ui";
import { API } from "@/config";
import { isTouchDevice, safePrint } from "@/components/admin/LabelPrintOverlay";

/* ==================== Admin: Customer Tags (Brother QL-820) ====================
 * Print-friendly page for delivery-day address tags. One tag per quantity item.
 *
 * Layout: 62mm continuous roll. Each tag is a self-contained page-break block
 * so the printer feeds one label at a time. Uses window.print() — user picks
 * the QL-820 in the OS print dialog and hits Print.
 *
 * Each tag now encodes the reservation UUID as a QR code (previously Code128).
 * QR beats 1D here because drivers can scan it with a plain phone camera —
 * no laser scanner needed — and it survives smudges/tears better at the same
 * label real estate. Human-readable short code (EGR-XXXXXX) still prints
 * underneath as a fallback for manual lookup.
 */
const LabelTag = ({ tag, index }) => {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (canvasRef.current && tag.barcode_value) {
      // High error-correction so a coffee-splash doesn't kill the label.
      QRCode.toCanvas(canvasRef.current, tag.barcode_value, {
        errorCorrectionLevel: "H",
        margin: 1,
        width: 132,
      }).catch((e) => console.warn("QR render failed:", e?.message));
    }
  }, [tag.barcode_value]);
  return (
    <div className="label-tag" data-testid={`label-tag-${index}`}>
      <div className="label-header">
        <span className="label-brand">Enchanted Gear Rentals</span>
        <span className="label-count">{tag.unit_number}/{tag.unit_total}</span>
      </div>
      <div className="label-product">{tag.product_name}</div>
      <div className="label-customer">{tag.customer_name}</div>
      <div className="label-address">{tag.address_line}</div>
      <div className="label-dates">
        <div><b>Deliver</b> {tag.delivery_date} · {tag.delivery_slot}</div>
        <div><b>Pickup</b> {tag.pickup_date} · {tag.pickup_slot}</div>
      </div>

      <div className="label-barcode-wrap" data-testid={`label-barcode-${index}`}>
        <canvas ref={canvasRef} className="label-qr" />
        <div className="label-shortcode" data-testid={`label-shortcode-${index}`}>{tag.short_code}</div>
      </div>
    </div>
  );
};


export const Labels = () => {
  const [sp, setSp] = useSearchParams();
  const initialDay = sp.get("day") || new Date().toISOString().slice(0, 10);
  const initialRes = sp.get("reservation_id") || "";
  const autoprint = sp.get("autoprint") === "1";
  const [day, setDay] = useState(initialDay);
  const [resId, setResId] = useState(initialRes);
  const [mode, setMode] = useState(initialRes ? "single" : "day");
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const autoPrintFiredRef = useRef(false);
  const [tapToPrint, setTapToPrint] = useState(false);

  // Selection + orientation state
  const [orientation, setOrientation] = useState("vertical");
  const [selected, setSelected] = useState(new Set()); // tag keys
  const [quantities, setQuantities] = useState({});    // tag key → copy count
  const [printing, setPrinting] = useState(false);

  const tagKey = (t) => `${t.reservation_id}:${t.product_id}:${t.unit_number}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = mode === "single" ? { reservation_id: resId } : { day };
      if (mode === "single" && !resId) { setTags([]); return; }
      const { data } = await axios.get(`${API}/admin/customer-tags`, { params });
      const loaded = data.tags || [];
      setTags(loaded);
      // Select all by default whenever tags reload
      setSelected(new Set(loaded.map(tagKey)));
      setQuantities({});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load tags.");
      setTags([]);
    } finally { setLoading(false); }
  }, [mode, day, resId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (mode === "day") next.set("day", day);
    if (mode === "single" && resId) next.set("reservation_id", resId);
    if (autoprint) next.set("autoprint", "1");
    setSp(next, { replace: true });
  }, [mode, day, resId, autoprint, setSp]);

  // Auto-print when arriving via `?autoprint=1` — fires once after tags load
  useEffect(() => {
    if (!autoprint || autoPrintFiredRef.current || loading || tags.length === 0) return;
    autoPrintFiredRef.current = true;
    if (isTouchDevice()) { setTapToPrint(true); return; }
    const t = setTimeout(() => { doPrint(); }, 450);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoprint, loading, tags.length]);

  const allSelected = tags.length > 0 && tags.every(t => selected.has(tagKey(t)));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(tags.map(tagKey)));
  };
  const toggleOne = (key) => setSelected(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });
  const setQty = (key, val) => {
    const n = Math.max(1, Math.min(10, parseInt(val, 10) || 1));
    setQuantities(prev => ({ ...prev, [key]: n }));
  };

  const selectedTags = tags.filter(t => selected.has(tagKey(t)));

  const doPrint = async () => {
    setTapToPrint(false);
    if (!selectedTags.length) { toast.warning("Select at least one tag."); return; }
    setPrinting(true);
    try {
      const qtys = {};
      selectedTags.forEach(t => {
        const k = tagKey(t);
        const n = quantities[k] || 1;
        if (n > 1) qtys[k] = n;
      });
      const resp = await axios.post(
        `${API}/admin/labels-pdf`,
        { tags: selectedTags, orientation, quantities: qtys },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "PDF generation failed.");
    } finally { setPrinting(false); }
  };

  // Group tags by reservation for display
  const grouped = useMemo(() => {
    const map = new Map();
    tags.forEach(t => {
      const key = t.reservation_id;
      if (!map.has(key)) map.set(key, { customer_name: t.customer_name, short_code: t.short_code, items: [] });
      map.get(key).items.push(t);
    });
    return [...map.entries()];
  }, [tags]);

  return (
    <div data-testid="admin-labels">
      {tapToPrint && tags.length > 0 && (
        <div className="print:hidden fixed bottom-0 inset-x-0 z-[90] bg-[#0B1B2B] border-t border-[#FFD166]/30 p-3 flex items-center gap-3 shadow-2xl" data-testid="labels-tap-to-print">
          <p className="text-sm text-[#F6F1E4] flex-1">{tags.length} tag{tags.length === 1 ? "" : "s"} ready.</p>
          <Btn onClick={doPrint} data-testid="labels-tap-to-print-btn"><Printer className="w-4 h-4" /> Open as PDF</Btn>
          <button type="button" onClick={() => setTapToPrint(false)} className="text-xs text-[#B9C8D5]" data-testid="labels-tap-to-print-dismiss">Dismiss</button>
        </div>
      )}

      {/* ── Page header ───────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3 print:hidden">
        <div>
          <Link to="/admin/reservations" className="text-xs text-[#B9C8D5] hover:text-[#FFD166] inline-flex items-center gap-1 mb-2"><ArrowLeft className="w-3 h-3" /> Back to reservations</Link>
          <h1 className="font-serif text-4xl">Labels</h1>
          <p className="text-sm text-[#B9C8D5]">Brother QL-810W · 62 mm continuous roll</p>
        </div>
        <div className="flex gap-2 items-end flex-col">
          {mode === "day" && day !== new Date().toISOString().slice(0, 10) && (
            <Btn variant="ghost" onClick={() => setDay(new Date().toISOString().slice(0, 10))} data-testid="labels-jump-today">Jump to today</Btn>
          )}
          <Btn onClick={doPrint} disabled={loading || printing || selectedTags.length === 0} data-testid="labels-print-btn">
            <Printer className="w-4 h-4" /> {printing ? "Generating…" : `Print ${selectedTags.length} tag${selectedTags.length === 1 ? "" : "s"} as PDF`}
          </Btn>
          {tags.length > 0 && (
            <p className="text-[11px] text-[#B9C8D5] text-right leading-snug">
              iPhone: tap PDF → Share → Print → select QL-810W<br />
              Auto-cut requires "Auto Cut" on in printer hardware settings
            </p>
          )}
        </div>
      </div>

      {/* Controls card */}
      <Card className="p-5 mb-4 print:hidden">
        {/* Mode selector */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="labels-mode">
          <button
            type="button"
            onClick={() => setMode("day")}
            className={`px-3 py-1.5 rounded-full border text-sm ${mode === "day" ? "border-[#FFD166] bg-[#FFD166]/10 text-[#FFD166]" : "border-[#B9C8D5]/30 text-[#B9C8D5] hover:border-[#FFD166]/40"}`}
            data-testid="labels-mode-day"
          ><CalIcon className="w-3.5 h-3.5 inline mr-1" /> Bulk by delivery day</button>
          <button
            type="button"
            onClick={() => setMode("single")}
            className={`px-3 py-1.5 rounded-full border text-sm ${mode === "single" ? "border-[#FFD166] bg-[#FFD166]/10 text-[#FFD166]" : "border-[#B9C8D5]/30 text-[#B9C8D5] hover:border-[#FFD166]/40"}`}
            data-testid="labels-mode-single"
          ><User className="w-3.5 h-3.5 inline mr-1" /> Single reservation</button>
        </div>

        {/* Date / reservation input */}
        <div className="grid md:grid-cols-2 gap-3 mb-4">
          {mode === "day" ? (
            <div>
              <label className="eyebrow">Delivery date</label>
              <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="mt-1" data-testid="labels-day-input" />
            </div>
          ) : (
            <div>
              <label className="eyebrow">Reservation ID</label>
              <Input value={resId} onChange={(e) => setResId(e.target.value)} placeholder="Reservation UUID" className="mt-1 font-mono text-xs" data-testid="labels-resid-input" />
            </div>
          )}
        </div>

        {/* Orientation toggle */}
        {tags.length > 0 && (
          <div className="flex items-center gap-2 mb-4" data-testid="labels-orientation">
            <span className="text-xs text-[#B9C8D5] font-medium">Format:</span>
            <button
              type="button"
              onClick={() => setOrientation("vertical")}
              className={`px-3 py-1 rounded-full text-xs border transition-colors ${orientation === "vertical" ? "border-[#FFD166] bg-[#FFD166]/10 text-[#FFD166]" : "border-[#B9C8D5]/30 text-[#B9C8D5] hover:border-[#FFD166]/40"}`}
              data-testid="labels-orientation-vertical"
            >Vertical (tall)</button>
            <button
              type="button"
              onClick={() => setOrientation("horizontal")}
              className={`px-3 py-1 rounded-full text-xs border transition-colors ${orientation === "horizontal" ? "border-[#FFD166] bg-[#FFD166]/10 text-[#FFD166]" : "border-[#B9C8D5]/30 text-[#B9C8D5] hover:border-[#FFD166]/40"}`}
              data-testid="labels-orientation-horizontal"
            >Horizontal (62×38mm)</button>
          </div>
        )}

        {/* Select all / deselect */}
        {tags.length > 0 && (
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={toggleAll}
              className="text-xs text-[#FFD166] hover:underline"
              data-testid="labels-select-all"
            >{allSelected ? "Deselect all" : "Select all"}</button>
            <span className="text-xs text-[#B9C8D5]">{selectedTags.length} / {tags.length} selected</span>
          </div>
        )}
      </Card>

      {loading ? (
        <div className="text-center text-[#B9C8D5] py-10 print:hidden">Loading…</div>
      ) : tags.length === 0 ? (
        <div className="text-center text-[#B9C8D5] py-16 print:hidden" data-testid="labels-empty">
          {mode === "day" ? `No paid deliveries scheduled for ${day} yet.` : "Enter a reservation ID above to load tags."}
        </div>
      ) : (
        <div className="space-y-4 print:hidden" data-testid="labels-selection-list">
          {grouped.map(([resId, group]) => (
            <Card key={resId} className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span className="font-mono text-[#FFD166] text-xs">{group.short_code}</span>
                  <span className="ml-2 text-sm font-medium text-[#F6F1E4]">{group.customer_name}</span>
                </div>
              </div>
              <div className="space-y-2">
                {group.items.map(t => {
                  const key = tagKey(t);
                  const checked = selected.has(key);
                  return (
                    <div
                      key={key}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg border transition-colors ${checked ? "border-[#FFD166]/40 bg-[#FFD166]/5" : "border-[#B9C8D5]/15 bg-white/2"}`}
                      data-testid={`label-row-${key}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleOne(key)}
                        className="w-4 h-4 accent-[#FFD166] cursor-pointer shrink-0"
                        data-testid={`label-check-${key}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-[#F6F1E4] truncate">{t.product_name}</div>
                        <div className="text-xs text-[#B9C8D5]">
                          Unit {t.unit_number}/{t.unit_total} · {t.delivery_date}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className="text-xs text-[#B9C8D5]">×</span>
                        <input
                          type="number"
                          min={1}
                          max={10}
                          value={quantities[key] || 1}
                          onChange={(e) => setQty(key, e.target.value)}
                          disabled={!checked}
                          className="w-12 px-1.5 py-1 rounded bg-black/30 border border-[#B9C8D5]/20 text-xs text-center text-[#F6F1E4] disabled:opacity-40"
                          data-testid={`label-qty-${key}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Hidden print sheet — kept for legacy window.print() fallback */}
      <div className="labels-sheet hidden print:block" data-testid="labels-sheet">
        {tags.map((t, i) => (
          <LabelTag key={t.unit_id || t.code || `label-${i}`} tag={t} index={i} />
        ))}
      </div>

      {/* Print CSS — 62 mm continuous roll · Brother QL-810W · one tag per cut */}
      <style>{`
        /* ─── Screen styles ─── */
        .labels-sheet { display: flex; flex-direction: column; gap: 12px; }
        .label-tag {
          width: 62mm;
          min-height: 40mm;
          padding: 3mm 4mm;
          background: #fff;
          color: #0A2438;
          font-family: "Helvetica Neue", Arial, sans-serif;
          border: 1px dashed #B9C8D5;
          border-radius: 4px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          box-sizing: border-box;
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .label-header { display: flex; justify-content: space-between; align-items: baseline; border-bottom: 1px solid #0A2438; padding-bottom: 1mm; margin-bottom: 2mm; }
        .label-brand { font-size: 8pt; font-weight: 700; letter-spacing: 0.5pt; text-transform: uppercase; }
        .label-count { font-size: 8pt; color: #666; }
        .label-product { font-size: 10pt; font-weight: 700; margin-bottom: 1.5mm; line-height: 1.25; word-break: break-word; overflow-wrap: break-word; }
        .label-customer { font-size: 14pt; font-weight: 700; line-height: 1.1; margin-bottom: 1mm; word-break: break-word; }
        .label-address { font-size: 9pt; line-height: 1.2; margin-bottom: 2mm; word-break: break-word; }
        .label-dates { font-size: 8pt; line-height: 1.35; border-top: 1px solid #ddd; padding-top: 1.5mm; }
        .label-phone { font-size: 8pt; color: #666; margin-top: 1mm; }
        .label-barcode-wrap { border-top: 1px dashed #B9C8D5; margin-top: 1.5mm; padding-top: 1.5mm; text-align: center; }
        .label-qr { width: 28mm; height: 28mm; display: block; margin: 0 auto; image-rendering: pixelated; }
        .label-shortcode { font-family: "Courier New", monospace; font-size: 9pt; font-weight: 700; letter-spacing: 1pt; color: #0A2438; margin-top: 1mm; }

        /* ─── Print: Brother QL-810W · 62 mm continuous roll ─── */
        @media print {

          /* 1. Page: roll width, content-height, zero margin
                margin: 0mm removes the browser's URL / date page footer */
          @page {
            size: 62mm auto;
            margin: 0mm;
          }

          /* 2. Nuke dark admin shell backgrounds all the way up the tree.
                Transparent on * + white on html/body = clean white canvas. */
          *, *::before, *::after {
            background-color: transparent !important;
            box-shadow: none !important;
            text-shadow: none !important;
          }
          html, body {
            background: white !important;
            width: 62mm !important;
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          /* 3. Hide admin sidebar, top nav, and all controls marked print:hidden */
          .admin-shell aside,
          .admin-shell > header,
          nav, header, footer,
          .print\\:hidden { display: none !important; }

          /* 4. Hide every direct sibling of the labels-sheet inside the page wrapper */
          [data-testid="admin-labels"] > *:not([data-testid="labels-sheet"]) {
            display: none !important;
          }

          /* 5. Remove admin main content padding */
          .admin-shell main, main {
            padding: 0 !important;
            margin: 0 !important;
          }

          /* 6. Labels sheet: single-column block, no screen gaps */
          .labels-sheet {
            display: block !important;
            width: 62mm !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          /* 7. Each tag = one printer page = one auto-cut on the QL-810W
                Both break-after (modern spec) and page-break-after (legacy/WebKit)
                are needed for maximum iOS Safari compatibility. */
          .label-tag {
            display: block !important;
            width: 62mm !important;
            max-width: 62mm !important;
            box-sizing: border-box !important;
            padding: 3mm 4mm !important;
            margin: 0 !important;
            background: white !important;
            color: #0A2438 !important;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            break-after: page !important;
            page-break-after: always !important;
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }

          /* 8. No trailing blank label after the last tag */
          .label-tag:last-child {
            break-after: avoid !important;
            page-break-after: avoid !important;
          }

          /* 9. Restore divider lines cleared by the transparent-background rule above */
          .label-header    { border-bottom: 1px solid #0A2438 !important; }
          .label-dates     { border-top: 1px solid #ddd !important; }
          .label-barcode-wrap { border-top: 1px dashed #B9C8D5 !important; }

          /* 10. Single QR code: centre it and give it generous room on the 62mm roll */
          .label-qr {
            width: 26mm !important;
            height: 26mm !important;
            max-width: 26mm !important;
            display: block !important;
            margin: 0 auto !important;
          }
        }
      `}</style>

    </div>
  );
};

export default Labels;
